import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  Checkbox,
} from '@mui/material';
import Box from '@mui/material/Box';
import {blue} from '@mui/material/colors';
import TextField from '@mui/material/TextField';
import React, {useState} from 'react';
import {
  Column,
  createColumn,
  ExpandableTable
} from '../../../../components/ExpandableTable';
import {FeatureTemplate} from '../../../../model/license/feature-template.model';
import '../../../../styles/global-tables.scss';
import {LicenseFeatureDetails} from '../../../../model/license/license-feature-details.model';
import {FormatUtils} from '../../../../utils/format.utils';

import {MassiveAction} from '../state/organization-details-page-state';

type Props = {
  massiveAction: MassiveAction
  featureStatusChanged: (id: string) => void
  featureSubscriptionTypeChanged: (id: string) => void
  featureTrialDateChanged: (id: string, trialDate: string) => void

  mainTableRows: FeatureTemplate[]
  getChildRows: (row: FeatureTemplate) => FeatureTemplate[];
  getFeatureDetails: (featureTemplateId: string) => LicenseFeatureDetails | null

  translate: (key: string) => string
  translateLocal: (key: string) => string

  isFeatureEnabledForPlanCreation: (id: string) => boolean
  isFeatureTemplateSelected: (id: string) => boolean
  onFeatureTemplateSelectionChanged: (id: string, checked: boolean) => void
}

export const OrganizationLicenseDetailsTable = ({
                                                  massiveAction,
                                                  featureStatusChanged,
                                                  featureSubscriptionTypeChanged,
                                                  featureTrialDateChanged,
                                                  isFeatureTemplateSelected,
                                                  onFeatureTemplateSelectionChanged,
                                                  isFeatureEnabledForPlanCreation,
                                                  mainTableRows,
                                                  getChildRows,
                                                  getFeatureDetails,
                                                  translate,
                                                  translateLocal
                                                }: Props) => {
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});

  const showSelectColumn = massiveAction === 'create_plan';

  const selectColumn: Column<FeatureTemplate> = createColumn({
    key: 'select',
    label: '',
    render: (row) => {
      if(isFeatureEnabledForPlanCreation(row.id)) {
        return <Checkbox
          checked={isFeatureTemplateSelected(row.id)}
          onChange={(e) => onFeatureTemplateSelectionChanged(row.id, e.target.checked)}
        />
      }
      return <></>
    },
  });

  const columns: Column<FeatureTemplate>[] = [
    createColumn({ key: 'featureType', label: translateLocal('column.featureType'), render: (row) => translate(`licenseDetails.feature.type.${row.featureType}`) }),
    createColumn({ key: 'activationCost', label: translateLocal('column.activationCost'), render: (row) =>
      row.activationCost === 0 ? '' : `${row.activationCost} €`
    }),
    createColumn({ key: 'acquisitionType', label: translateLocal('column.acquisitionType'), render: (row) => {
        const featureDetails = getFeatureDetails(row.id)
        if(featureDetails === null) return ''



        return featureDetails.isDefault
          ? translateLocal('acquisitionType.purchase')
          : <Box display="flex" alignItems="center" gap={1}>
              {translateLocal('acquisitionType.extend-purchase')} <InfoOutlinedIcon sx={{fontSize: 16, color: blue[500]}}/>
            </Box>
      }
    }),
    createColumn({
      key: 'subscriptionType', label: translateLocal('column.subscriptionType'), render: (row) => {
        const featureDetails = getFeatureDetails(row.id)
        if(featureDetails === null) return ''

        if(massiveAction === 'update_subscription_type') {
          return <Box display="flex" alignItems="center" gap={1}>
            <RefreshIcon sx={{ fontSize: 18, color: blue[500], cursor: 'pointer'}} onClick={() => featureSubscriptionTypeChanged(row.id)} />
            {translate(`subscriptionType.${featureDetails.subscriptionType}`)}
          </Box>
        }
        return translate(`subscriptionType.${featureDetails.subscriptionType}`);
      }
    }),
    createColumn({ key: 'expiredDate', label: translateLocal('column.expiredDate'), render: (row) => {
        const featureDetails = getFeatureDetails(row.id)
        if(featureDetails === null || featureDetails.subscriptionType === 'PAID') return ''

        if(massiveAction === 'update_trial_date') {
          return <Box display="flex" alignItems="center" gap={1}>
            <TextField
              type="date"
              value={featureDetails.trialExpiredDate}
              onChange={(e) => featureTrialDateChanged(row.id, e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                sx: {
                  '& input': {
                    padding: '4px 8px',
                    height: '28px',
                  },
                },
              }}
            />
          </Box>
        }

        return FormatUtils.formatDate(featureDetails.trialExpiredDate)
      }
    }),
    createColumn({ key: 'status', label: translateLocal('column.status'), render: (row) => {
        const featureDetails = getFeatureDetails(row.id)
        if(featureDetails === null) return ''

        if(massiveAction === 'update_status') {
          return <Box display="flex" alignItems="center" gap={1}>
            <RefreshIcon sx={{ fontSize: 18, color: blue[500], cursor: 'pointer'}} onClick={() => featureStatusChanged(row.id)} />
            {translateLocal(`status.${featureDetails.featureStatus}`)}
          </Box>
        }

        return translateLocal(`status.${featureDetails.featureStatus}`)
      }
    }),
  ];

  return <ExpandableTable
    rows={mainTableRows}
    columns={showSelectColumn ? [selectColumn, ...columns] : columns}
    getChildRows={getChildRows}
    isRowOpen={(row) => openRows[row.id] || false}
    onRowToggle={(row) => setOpenRows((prev) => ({ ...prev, [row.id]: !prev[row.id] }))}
    rowKey={(row) => row.id}
    renderParentRow={(row) => translate(`licenseDetails.feature.module.${row.featureModule}`)}
  />
}